import { create } from 'zustand';

/**
 * Selected-client store — the client whose engagements the left sidebar
 * lists under its "Engagements" section.
 *
 * Why this exists:
 *   - The sidebar used to list every engagement across every client. With
 *     more than a handful of clients that section became the noisiest
 *     part of the shell. The Clients page already lists every client, so
 *     the sidebar now scopes to one client and the Clients page is where
 *     you switch.
 *
 * How the selection is set (all from the sidebar itself, see
 * `components/layout/sidebar.tsx`):
 *   - Opening a client detail page (`/clients/:clientId`) selects it.
 *   - Opening any engagement selects that engagement's client, so deep
 *     links and portfolio-card clicks always land with the right client
 *     in the sidebar.
 *   - Otherwise the last selection is reused, persisted in localStorage
 *     under `caliber.selected_client_id` so a reload keeps it.
 *
 * Why Zustand + localStorage: matches the auth store convention (same
 * `caliber.*` key namespace, same `create()` shape).
 */

const STORAGE_KEY = 'caliber.selected_client_id';

function readPersisted(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePersisted(clientId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (clientId) window.localStorage.setItem(STORAGE_KEY, clientId);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked storage — the in-memory value still works
    // for the session.
  }
}

export interface SelectedClientState {
  selectedClientId: string | null;
  setSelectedClientId: (clientId: string | null) => void;
}

export const useSelectedClient = create<SelectedClientState>((set) => ({
  selectedClientId: readPersisted(),
  setSelectedClientId: (clientId) => {
    writePersisted(clientId);
    set({ selectedClientId: clientId });
  },
}));
